/**
 * Presets de terminologie BTP — Cas C de Phase 4 white-label.
 *
 * Chaque preset est une override partielle des clés i18n de la locale courante.
 * Au runtime, ils sont mergés DANS L'ORDRE :
 *
 *   1. Locale standard (ex. fr-CH/roles.json)
 *   2. Preset choisi par le tenant       ← écrase les clés présentes ici
 *   3. tenant_theme.terminology_overrides ← écrase encore (Cas A)
 *
 * Donc on ne définit ici QUE les clés à overrider. Tout le reste reste i18n standard.
 *
 * Convention de clés : "namespace.section.key" (même format que dans /locales/).
 */

export type TerminologyPresetName =
  | 'sds_default'
  | 'france_tpe'
  | 'btp_corporate'
  | 'artisans';

export interface TerminologyPreset {
  name: TerminologyPresetName;
  label: string;
  description: string;
  /** Overrides : { 'namespace.key': 'Nouveau libellé' } */
  overrides: Record<string, string>;
}

export const TERMINOLOGY_PRESETS: Record<TerminologyPresetName, TerminologyPreset> = {
  // ============================================================================
  // SDS Default — terminologie de référence (= libellés des locales sources)
  // ============================================================================
  sds_default: {
    name: 'sds_default',
    label: 'SDS Default',
    description: 'Vocabulaire BTP français de référence — chantiers, compagnons, fiches d\'heures.',
    overrides: {},
  },

  // ============================================================================
  // France TPE — vocabulaire des petites entreprises BTP françaises
  // ============================================================================
  france_tpe: {
    name: 'france_tpe',
    label: 'France TPE',
    description: 'Petites entreprises BTP françaises : ouvriers, chef de chantier classique.',
    overrides: {
      'roles.compagnon': 'Ouvrier',
      'roles.chef': 'Chef de chantier',
      'roles.conducteur': 'Conducteur de travaux',
      'roles.client_final': 'Maître d\'ouvrage',
      'home.kpi.compagnons_pointes': 'Ouvriers pointés',
      'home.kpi.fiches_a_valider': 'Feuilles à valider',
    },
  },

  // ============================================================================
  // BTP Corporate — entreprises moyennes structurées (DRH, contrôle de gestion)
  // ============================================================================
  btp_corporate: {
    name: 'btp_corporate',
    label: 'BTP Corporate',
    description: 'Entreprises structurées avec DRH et contrôle de gestion. Vocabulaire RH formel.',
    overrides: {
      'roles.compagnon': 'Collaborateur terrain',
      'roles.chef': 'Responsable de site',
      'roles.conducteur': 'Chef de projet',
      'roles.direction': 'Direction générale',
      'roles.client_final': 'Donneur d\'ordre',
      'home.kpi.compagnons_pointes': 'Collaborateurs présents',
      'home.kpi.fiches_a_valider': 'Pointages à valider',
      'common.nav.logout': 'Quitter la session',
    },
  },

  // ============================================================================
  // Artisans — petites équipes, vocabulaire familier
  // ============================================================================
  artisans: {
    name: 'artisans',
    label: 'Artisans',
    description: 'Petites équipes artisanales, vocabulaire familier proche du terrain.',
    overrides: {
      'roles.compagnon': 'Compagnon',
      'roles.chef': 'Chef d\'équipe',
      'roles.conducteur': 'Patron',
      'roles.admin': 'Patron',
      'roles.client_final': 'Client',
      'home.kpi.chantiers_actifs': 'Chantiers en cours',
      'home.kpi.compagnons_pointes': 'Compagnons sur site',
      'home.kpi.fiches_a_valider': 'Heures à valider',
    },
  },
};

export const TERMINOLOGY_PRESET_LIST = Object.values(TERMINOLOGY_PRESETS);

export const DEFAULT_TERMINOLOGY_PRESET: TerminologyPresetName = 'sds_default';
